import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import SmartForm from '@component/smartForm/SmartForm';
import SmartTextInput from '@component/smartForm/SmartInputText';
import SmartContainer from './smartForm/SmartContainer';
import SmartTextareaInput from './smartForm/SmartTextareaInput';
import SmartSwitchInput from './smartForm/SmartSwitchInput';

import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";

import { useEventByUuidQuery } from "@query/eventQuery";
import { useClubEventQuery } from "@query/clubQuery";
import { useClubEventUpdateMutation, useCreateClubEventMutation } from "@mutation/clubMutation";
import { mapRewardedActivityEvent, RewardedActivityEvents } from '@service/rewardedActivityService';

import ClubTimeRuleList from "@component/ClubTimeRuleList"
import SiteTimeRuleList from "@component/SiteTimeRuleList"

// Context
import { AccountContext } from "@context/AccountProvider";
import { AlertContext } from "@context/AlertProvider";

const initModel = {
    eventUuid: null,
    name: null,
    description: null,
    imageSrc: null,
    reward: 0,
    clubEventUuid: null,
    isRewardActive: false,
    mappedEvent: null
};

// 👈 1. Added onCancel prop for integration with the parent side panel
function ClubEventForm({ eventUuid, onCancel }) {
    const { club, sites } = useContext(AccountContext);
    const { addAlertBanner } = useContext(AlertContext);
    const { t } = useTranslation(['main', 'rewardedActivity']);

    const eventByUuidQuery = useEventByUuidQuery(eventUuid);
    const clubEventQuery = useClubEventQuery(club?.uuid, eventUuid);
    const clubEventUpdateMutation = useClubEventUpdateMutation();
    const createClubEventMutation = useCreateClubEventMutation();

    const [clubEvent, setClubEvent] = useState(null);
    const [model, setModel] = useState(initModel);

    useEffect(() => {
        if (clubEventQuery.data?.data) {
            const clubEventData = clubEventQuery.data.data;
            setClubEvent(clubEventData);
            setModel(prev => ({
                ...prev,
                reward: clubEventData.reward,
                clubEventUuid: clubEventData.uuid,
                isRewardActive: true
            }));
        }

        if (eventByUuidQuery.data?.data) {
            const event = eventByUuidQuery.data.data;
            const mappedEvent = mapRewardedActivityEvent(event.name);
            setModel(prev => ({
                ...prev,
                eventUuid: event.uuid,
                name: t(`rewardedActivity:${mappedEvent.translationKey}`),
                description: t(`rewardedActivity:description.${mappedEvent.translationKey}`),
                imageSrc: mappedEvent.imageSrc,
                mappedEvent: mappedEvent
            }));
        }
    }, [t, clubEventQuery.data, eventByUuidQuery.data]);

    useEffect(() => {
        if (model?.isRewardActive === false) {
            setModel(prev => ({ ...prev, reward: 0 }));
            if (!model?.reward) {
                setModel(prev => ({ ...prev, isRewardActive: false }));
            }
        }
    }, [model?.isRewardActive, model?.reward]);

    function handleSave(newModel) {
        const mutationOptions = {
            onSuccess: () => {
                addAlertBanner(t("main:saveSuccess"), "success");
                clubEventQuery.refetch(); // Refetch data to show the latest state
            },
            onError: (error) => {
                if (error.status === 403) {
                    addAlertBanner(t("main:noPermissionLong"), 'error');
                } else {
                    addAlertBanner(t("main:saveError"), "error");
                }
            }
        };

        if (clubEvent) {
            // UPDATE existing ClubEvent
            clubEventUpdateMutation.mutate({
                data: {
                    clubUuid: club.uuid,
                    clubEventUuid: newModel.clubEventUuid,
                    reward: newModel.reward
                }
            }, mutationOptions);
        } else {
            // CREATE new ClubEvent
            createClubEventMutation.mutate({
                data: {
                    clubUuid: club.uuid,
                    eventUuid: newModel.eventUuid,
                    reward: newModel.reward // 👈 Added reward to the creation payload
                }
            }, mutationOptions);
        }
    }

    // 👈 2. Consolidate all loading states for a better user experience
    const isLoading = eventByUuidQuery.isFetching ||
        clubEventQuery.isFetching ||
        clubEventUpdateMutation.isPending ||
        createClubEventMutation.isPending;


    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <SmartForm
                formModel={model}
                setFormModel={setModel}
                onSave={handleSave}
                onCancel={onCancel} // 👈 3. Pass the onCancel prop directly to SmartForm
                isLoading={isLoading} // 👈 Use the consolidated loading state
                isEdit={true}
                buttonPanelSx={{
                    top: "10px",
                    right: "10px",
                }}
                contentSx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    p: 0
                }}
            >
                <img
                    src={model?.imageSrc}
                    alt={model?.name}
                    style={{
                        width: '100%',
                        height: '250px',
                        objectFit: 'cover',
                    }}
                />
                <SmartContainer contentSx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    px: 2
                }}>
                    <SmartTextInput
                        editable={false}
                        field="name"
                        // label={t('rewardedActivity:activityName')}
                        valueSx={{ fontSize: "1.75rem" }}
                    />
                    <SmartTextareaInput
                        editable={false}
                        field="description"
                        // label={t('rewardedActivity:description')}
                        valueSx={{
                            fontSize: "1rem",
                            fontWeight: 400
                        }}
                    />
                    <SmartContainer contentSx={{
                        display: "flex",
                        flexDirection: "row",
                        p: 0,
                        gap: 1
                    }}>
                        <SmartSwitchInput
                            sx={{ flex: 1 }}
                            field="isRewardActive"
                            label={t('rewardedActivity:isRewardActive')}
                        />
                        <SmartTextInput
                            sx={{ flex: 1 }}
                            type="number"
                            field="reward"
                            label={t('rewardedActivity:pointsReward')}
                            editable={model.isRewardActive}
                        />
                    </SmartContainer>
                </SmartContainer>

            </SmartForm>
            <Divider flexItem />
            {
                model?.isRewardActive &&
                model?.reward > 0 &&
                model?.mappedEvent &&
                model.mappedEvent.name === RewardedActivityEvents.TIME_RULE.name &&
                (
                    <>
                        <ClubTimeRuleList clubUuid={club.uuid} />
                        {sites.map((site) => (
                            <SiteTimeRuleList siteName={site.name} siteUuid={site.uuid} />
                        ))}
                    </>
                )
            }

        </Box>
    );
}

export default ClubEventForm;